/**
 * ext-components-base
 *
 * Adapptio: Set of base components
 *
 * @package ext-components-base
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { ItemRegistry } from "@adapptio/lib-shared";
import { ITranslationManager } from "@adapptio/lib-core";
import {
	termsEditor as libTermsEditor,
	termsRuntime as libTermsRuntime,
	TGenericViewComponentDefinition
} from "@adapptio/lib-components";

import { termsEditor, termsRuntime } from "./terms";

import { ComponentAppHeader } from "./Components/AppHeader/AppHeader";
import { ComponentBox } from "./Components/Box/Box";
import { ComponentButton } from "./Components/Button/Button";
import { ComponentButtonGroup } from "./Components/ButtonGroup/ButtonGroup";
import { ComponentCheckboxField } from "./Components/Field/CheckboxField";
import { ComponentColorField } from "./Components/Field/ColorField";
import { ComponentComposedChart } from "./Components/ComposedChart/ComposedChart";
import { ComponentContainer } from "./Components/Container/Container";
import { ComponentDataTable } from "./Components/DataTable/DataTable";
import { ComponentDatetimeField } from "./Components/Field/DatetimeField";
import { ComponentFileField } from "./Components/Field/FileField";
import { ComponentForm } from "./Components/Form/Form";
import { ComponentGaugeChart } from "./Components/GaugeChart/GaugeChart";
import { ComponentGrid } from "./Components/Grid/Grid";
import { ComponentIcon } from "./Components/Icon/Icon";
import { ComponentIframe } from "./Components/Iframe/Iframe";
import { ComponentImage } from "./Components/Image/Image";
import { ComponentLabel } from "./Components/Label/Label";
import { ComponentMenuButton } from "./Components/MenuButton/MenuButton";
import { ComponentNumberField } from "./Components/Field/NumberField";
import { ComponentOneOf } from "./Components/OneOf/OneOf";
import { ComponentOptionsField } from "./Components/Field/OptionsField";
import { ComponentPagination } from "./Components/Pagination/Pagination";
import { ComponentPixelGrid } from "./Components/PixelGrid/PixelGrid";
import { ComponentPropertyItem } from "./Components/PropertyItem/PropertyItem";
import { ComponentQrCode } from "./Components/QrCode/QrCode";
import { ComponentRangeField } from "./Components/Field/RangeField";
import { ComponentRepeat } from "./Components/Repeat/Repeat";
import { ComponentRoutes } from "./Components/Routes/Routes";
import { ComponentSidebarLayout } from "./Components/SidebarLayout/SidebarLayout";
import { ComponentSpacer } from "./Components/Spacer/Spacer";
import { ComponentTable, ComponentAdvancedTable } from "./Components/Table/Table";
import { ComponentText } from "./Components/Text/Text";
import { ComponentTextAreaField } from "./Components/Field/TextAreaField";
import { ComponentTextField } from "./Components/Field/TextField";
import { ComponentTextField_v2 } from "./Components/Field/TextField_v2";
import { ComponentTreeNavigation } from "./Components/TreeNavigation/TreeNavigation";
import { ComponentView } from "./Components/View/View";
import { ComponentQrCodeReader } from "./Components/QrCodeReader/QrCodeReader";

import { ComponentDatePicker } from "../../ext-components-fluent-ui/src/Components/DatePicker/DatePicker";
import { ComponentTimePicker } from "../../ext-components-fluent-ui/src/Components/TimePicker/TimePicker";
import { ComponentFluentTheme } from "../../ext-components-fluent-ui/src/Components/FluentThemeProvider/FluentThemeProvider";

// Export registration functions
export function registerComponents(
	registry: ItemRegistry<TGenericViewComponentDefinition>,
	translationManager: ITranslationManager
): void {
	// Layout components
	registry.register(ComponentAppHeader);
	registry.register(ComponentBox);
	registry.register(ComponentButtonGroup);
	registry.register(ComponentContainer);
	registry.register(ComponentGrid);
	registry.register(ComponentSidebarLayout);

	// Logic components
	registry.register(ComponentOneOf);
	registry.register(ComponentRoutes);
	registry.register(ComponentRepeat);
	registry.register(ComponentForm);

	// Navigation components
	registry.register(ComponentPagination);
	registry.register(ComponentTreeNavigation);

	// Content components
	registry.register(ComponentAdvancedTable);
	registry.register(ComponentButton);
	registry.register(ComponentDataTable);
	registry.register(ComponentIcon);
	registry.register(ComponentIframe);
	registry.register(ComponentImage);
	registry.register(ComponentLabel);
	registry.register(ComponentMenuButton);
	registry.register(ComponentQrCode);
	registry.register(ComponentQrCodeReader);
	registry.register(ComponentText);
	registry.register(ComponentView);
	registry.register(ComponentPropertyItem);
	registry.register(ComponentSpacer);

	// Field components
	registry.register(ComponentCheckboxField);
	registry.register(ComponentColorField);
	registry.register(ComponentDatetimeField);
	registry.register(ComponentFileField);
	registry.register(ComponentTextField_v2);
	registry.register(ComponentTextAreaField);
	registry.register(ComponentNumberField);
	registry.register(ComponentRangeField);
	registry.register(ComponentOptionsField);

	// Charts components
	registry.register(ComponentComposedChart);
	registry.register(ComponentGaugeChart);
	registry.register(ComponentPixelGrid);

	registry.register(ComponentDatePicker);
	registry.register(ComponentTimePicker);

	// Deprecated (hidden) components
	registry.register(ComponentTable);
	registry.register(ComponentTextField);
	registry.register(ComponentFluentTheme);

	// Register translation terms
	translationManager.registerTerms("editor", termsEditor, "ext-components-base");
	translationManager.registerTerms("runtime", termsRuntime, "ext-components-base");
	translationManager.registerTerms("editor", libTermsEditor, "ext-components-base#lib");
	translationManager.registerTerms("runtime", libTermsRuntime, "ext-components-base#lib");
}
